import { Badge, Box, Grid, Paper, Tabs, TextField } from "@mui/material";
import { styled } from "@mui/material/styles";
import { BORDER } from "./constants";

export const MessagingComposerContainer = styled(Box)(() => ({
  position: "relative",
  flex: "0 0 auto",
  display: "flex",
  flexDirection: "column",
  maxHeight: "40%",
  backgroundColor: "#F5F5F5",
  border: BORDER,
  borderRadius: 1,
  overflow: "hidden",
}));

export const MessagingComposerToolbar = styled(Box)({
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
  flexShrink: 0,
  minHeight: 40,
  padding: "0 4px",
  borderTop: BORDER,
});

export const MessagingComposerTitle = styled(Box)({
  flexShrink: 0,
  boxSizing: "border-box",
  minHeight: 40,
  padding: "8.5px 16px",
  borderBottom: BORDER,
  overflow: "hidden",
  textOverflow: "ellipsis",
  whiteSpace: "nowrap",
  fontSize: "16px",
  lineHeight: "23px",
});

export const MessagingComposerTitleField = styled(TextField)(({ theme }) => ({
  flexShrink: 0,
  "& .MuiOutlinedInput-root": {
    borderRadius: 0,
    backgroundColor: "transparent",
  },
  "&& .MuiOutlinedInput-root:not(.Mui-focused) .MuiOutlinedInput-notchedOutline":
    {
      borderStyle: "solid !important",
      borderWidth: "0 0 1px 0 !important",
      borderColor: "#E3E4E7 !important",
    },
  "&& .MuiOutlinedInput-root.Mui-focused fieldset": {
    borderStyle: "solid !important",
    borderWidth: "2px !important",
    borderColor: `${theme.palette.primary.main} !important`,
  },
  "& .MuiInputBase-input": {
    padding: "8.5px 16px",
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
  },
}));

export const MessagingComposerPendingBar = styled(Box)({
  flexShrink: 0,
  display: "flex",
  flexDirection: "row",
  flexWrap: "wrap",
  alignItems: "flex-start",
  gap: "6px 12px",
  padding: "4px 12px 8px",
  minHeight: 0,
});

export const ConversationThreadTabsStyled = styled(Tabs, {
  shouldForwardProp: (prop) => prop !== "compact",
})<{ compact?: boolean }>(({ theme, compact }) => {
  const height = compact ? 36 : 56;
  return {
    minHeight: height,
    height: "100%",
    width: "100%",
    overflow: "visible",
    "& .MuiTabs-scroller": {
      height: "100%",
      overflow: "visible !important",
    },
    "& .MuiTabs-flexContainer": {
      height: "100%",
    },
    "& .MuiTabs-indicator": {
      height: 2,
      bottom: 0,
    },
    "& .MuiTab-root": {
      textTransform: "none",
      fontWeight: 700,
      fontSize: compact ? "13px" : "14px",
      lineHeight: 1.2,
      height,
      minHeight: height,
      minWidth: "auto",
      maxWidth: "none",
      width: "auto",
      flexGrow: 0,
      flexShrink: 0,
      flexBasis: "auto",
      whiteSpace: "nowrap",
      overflow: "visible",
      padding: compact ? "0 12px" : "0 16px",
      color: theme.palette.text.secondary,
    },
    "& .MuiTab-root.Mui-selected": {
      color: theme.palette.primary.main,
    },
  };
});

export const MessagingInboxTabsStyled = styled(Tabs)(({ theme }) => ({
  minHeight: 56,
  width: "100%",
  "& .MuiTabs-flexContainer": {
    width: "100%",
  },
  "& .MuiTab-root": {
    textTransform: "capitalize",
    fontWeight: 700,
    fontSize: "14px",
    height: "56px",
    minHeight: 56,
    minWidth: "max-content",
    maxWidth: "none",
    width: "auto",
    flexGrow: 1,
    flexShrink: 0,
    flexBasis: "auto",
    whiteSpace: "nowrap",
    overflow: "hidden",
    paddingLeft: "8px",
    paddingRight: "8px",
    color: theme.palette.text.secondary,
  },
  "& .MuiTab-root.Mui-selected": {
    color: theme.palette.primary.main,
  },
}));

export const MessagingInboxUnreadBadge = styled(Badge)(({ theme }) => ({
  display: "inline-flex",
  alignItems: "center",
  "& .MuiBadge-badge": {
    position: "static",
    transform: "none",
    marginLeft: 6,
    backgroundColor: theme.palette.primary.light,
    color: "#fff",
    fontWeight: 600,
    fontSize: "14px",
    height: 22,
    minWidth: 22,
    padding: "0 5px",
    borderRadius: "50%",
  },
  "& .MuiBadge-badge.MuiBadge-invisible": {
    display: "none",
  },
}));

export const MessagingInboxListContainerStyled = styled(Grid)(() => ({
  flexDirection: "column",
  height: "calc(100% - 112px)",
  overflow: "hidden",
}));

export const MessagesContainerStyled = styled(Grid, {
  shouldForwardProp: (prop) => prop !== "disablePadding",
})<{ disablePadding?: boolean }>(({ disablePadding }) => ({
  display: "flex",
  flexDirection: "column",
  flex: 1,
  minHeight: 0,
  height: "100%",
  overflow: "hidden",
  padding: disablePadding ? 0 : "16px",
  gap: disablePadding ? 0 : "48px",
}));

export const FloatingChatButtonContainer = styled(Box)(({ theme }) => ({
  position: "fixed",
  bottom: 24,
  right: 24,
  zIndex: theme.zIndex.drawer + 1,
  "& .MuiBadge-badge": {
    height: 16,
    width: 16,
    minWidth: 16,
    padding: 0,
    borderRadius: "50%",
  },
}));

export const FloatingChatWindowContainer = styled(Paper)(({ theme }) => ({
  position: "fixed",
  bottom: 24,
  right: 24,
  width: 380,
  height: 676,
  maxWidth: "calc(100vw - 48px)",
  maxHeight: "calc(100vh - 96px)",
  display: "flex",
  flexDirection: "column",
  zIndex: theme.zIndex.drawer + 1,
  overflow: "hidden",
  borderRadius: "8px",
}));

export const FloatingChatWindowHeader = styled(Box)({
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
  padding: "8px 8px 8px 16px",
  borderBottom: BORDER,
  flexShrink: 0,
});

export const FloatingChatWindowBody = styled(Box)({
  flex: 1,
  minHeight: 0,
  padding: "12px 0px 0px 0px",
  overflow: "hidden",
  display: "flex",
  flexDirection: "column",
});

export const FloatingChatWindowFooter = styled(Box)({
  display: "flex",
  justifyContent: "flex-end",
  padding: "8px 16px 16px",
  flexShrink: 0,
  borderTop: BORDER,
});
